import './solicitacao-form-modal.js';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { ClienteAutenticado, Pedido, PedidoComItens, PedidoEventoPublico, Garantia, Ticket } from '@pgcustom/shared-types';
import { ROTULO_STATUS_OPERACIONAL } from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgTableRow } from '@pgcustom/design-system';
import { logger } from '../../core/logger.js';
import type { Resultado } from '../../core/resultado.js';
import { rotuloStatusPedido } from '../pedidos/status-pedido.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { PortalClienteApi } from './portal-cliente.api.js';
import type { ErpSolicitacaoFormModal } from './solicitacao-form-modal.js';

const ROTULO_STATUS_TICKET: Record<Ticket['status'], string> = {
  aberto: 'Aberto',
  fechado: 'Fechado',
};

const ROTULO_CANAL_TICKET: Record<Ticket['canal'], string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  site: 'Site',
};

/**
 * `<erp-portal-cliente-page>` — painel único do cliente autenticado.
 * Mostra só "meus pedidos", "minhas garantias" e "minhas solicitações"
 * — nunca um id de cliente recebido de fora, nunca dado de outro
 * cliente, nunca acesso a módulos administrativos do ERP interno (a
 * API por trás, `/portal-clientes/me/*`, já resolve ownership pelo
 * cookie de sessão). Layout próprio, deliberadamente sem
 * `<erp-shell>`/sidebar do ERP interno — o Portal do Cliente nunca deve
 * dar a impressão de acesso ao painel administrativo. Mesmo padrão
 * estrutural de `<erp-portal-parceiro-page>`.
 */
export class ErpPortalClientePage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _portalApi: PortalClienteApi | null = null;
  private _cliente: ClienteAutenticado | null = null;
  private _aoSair: (() => void) | null = null;

  private pedidos: Pedido[] = [];
  private resultadoPedidos: Resultado<Pedido[]> = { status: 'carregando' };
  private resultadoGarantias: Resultado<Garantia[]> = { status: 'carregando' };
  private resultadoSolicitacoes: Resultado<Ticket[]> = { status: 'carregando' };

  private pedidoSelecionadoId: string | null = null;
  private pedidoDetalhe: PedidoComItens | null = null;
  /** Timeline pública (status + mensagem da PG Custom) — nunca a nota interna, nem no tipo. */
  private pedidoEventos: PedidoEventoPublico[] = [];
  private carregandoDetalhe = false;
  private erroDetalhe = '';

  private modalSolicitacao!: ErpSolicitacaoFormModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set portalApi(valor: PortalClienteApi) {
    this._portalApi = valor;
  }

  set cliente(valor: ClienteAutenticado) {
    this._cliente = valor;
  }

  set aoSair(valor: () => void) {
    this._aoSair = valor;
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    void this.carregarPedidos();
    void this.carregarGarantias();
    void this.carregarSolicitacoes();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host {
          display: block;
          min-height: 100vh;
          background: var(--pg-color-bg-base, #121417);
          font-family: var(--pg-font-familia-base, system-ui);
        }
        header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--pg-space-4, 16px) var(--pg-space-6, 32px);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
        }
        .marca {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-lg, 1.125rem);
          font-weight: var(--pg-font-weight-bold, 700);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .marca span { color: var(--pg-color-dourado, #c9a24b); }
        .subtitulo-marca {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .conta {
          display: flex;
          align-items: center;
          gap: var(--pg-space-4, 16px);
        }
        .conta-nome {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .sair {
          background: none;
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          font-size: var(--pg-font-size-xs, 0.75rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .sair:hover { color: var(--pg-color-texto-primario, #f2f3f5); border-color: var(--pg-color-texto-secundario, #9aa1ab); }
        main { padding: var(--pg-space-6, 32px); }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0 0 var(--pg-space-5, 24px);
        }
        .secoes {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: var(--pg-space-4, 16px);
        }
        .detalhe { margin-top: var(--pg-space-4, 16px); }
        .detalhe-linha { display: flex; justify-content: space-between; padding: var(--pg-space-2, 8px) 0; border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); font-size: var(--pg-font-size-sm, 0.875rem); }
        .detalhe-linha span:first-child { color: var(--pg-color-texto-secundario, #9aa1ab); }
        .detalhe-linha span:last-child { color: var(--pg-color-texto-primario, #f2f3f5); }
        .detalhe-rodape { display: flex; justify-content: flex-end; margin-top: var(--pg-space-3, 12px); }
        .erro-detalhe { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); margin-top: var(--pg-space-3, 12px); }
        .erro-detalhe:empty { display: none; }
        .timeline-eventos { margin-top: var(--pg-space-4, 16px); }
        .timeline-eventos h4 {
          margin: 0 0 var(--pg-space-2, 8px);
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .timeline-lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); }
        .timeline-lista li { padding-bottom: var(--pg-space-3, 12px); border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); }
        .timeline-lista li:last-child { border-bottom: none; padding-bottom: 0; }
        .timeline-cabecalho { display: flex; justify-content: space-between; gap: var(--pg-space-2, 8px); font-size: var(--pg-font-size-sm, 0.875rem); }
        .timeline-status { font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-texto-primario, #f2f3f5); }
        .timeline-data { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-xs, 0.75rem); white-space: nowrap; }
        .timeline-mensagem { margin: var(--pg-space-1, 4px) 0 0; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
      </style>
      <header>
        <div>
          <div class="marca">PG <span>Custom</span></div>
          <div class="subtitulo-marca">Portal do Cliente</div>
        </div>
        <div class="conta">
          <span class="conta-nome">${escaparHtml(this._cliente?.nome ?? '')}</span>
          <button class="sair" type="button" id="botao-sair">Sair</button>
        </div>
      </header>
      <main>
        <h1>Olá, ${escaparHtml(primeiroNome(this._cliente?.nome ?? ''))}</h1>
        <div class="secoes">
          <pg-card>
            <span slot="header">Meus pedidos</span>
            <div id="secao-pedidos">
              <pg-empty-state variant="carregando" titulo="Meus pedidos"></pg-empty-state>
            </div>
          </pg-card>
          <pg-card>
            <span slot="header">Minhas garantias</span>
            <div id="secao-garantias">
              <pg-empty-state variant="carregando" titulo="Minhas garantias"></pg-empty-state>
            </div>
          </pg-card>
          <pg-card>
            <span slot="header">Minhas solicitações</span>
            <pg-button slot="header-acao" id="botao-nova-solicitacao" variant="secondary">Nova solicitação</pg-button>
            <div id="secao-solicitacoes">
              <pg-empty-state variant="carregando" titulo="Minhas solicitações"></pg-empty-state>
            </div>
          </pg-card>
        </div>
        <pg-card class="detalhe" id="card-detalhe" style="display:none">
          <span slot="header">Detalhe do pedido</span>
          <div id="secao-detalhe"></div>
        </pg-card>
      </main>
      <erp-solicitacao-form-modal id="modal-solicitacao"></erp-solicitacao-form-modal>
    `;

    this.root.querySelector('#botao-sair')?.addEventListener('click', () => this._aoSair?.());

    this.modalSolicitacao = this.root.querySelector('#modal-solicitacao') as ErpSolicitacaoFormModal;
    if (this._portalApi) this.modalSolicitacao.portalApi = this._portalApi;
    this.modalSolicitacao.aoSalvar = () => void this.carregarSolicitacoes();
    this.root.querySelector('#botao-nova-solicitacao')?.addEventListener('pg-click', () => this.modalSolicitacao.abrir());

    this.root.querySelector('#secao-pedidos')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const pedido = this.pedidos[index];
      if (pedido) void this.abrirDetalhePedido(pedido.id);
    });

    this.atualizarPedidos();
    this.atualizarGarantias();
    this.atualizarSolicitacoes();
    this.atualizarDetalhe();
  }

  private async carregarPedidos(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const pedidos = await api.meusPedidos();
      this.resultadoPedidos = pedidos.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: pedidos };
    } catch (erro) {
      logger.erro('portal do cliente: falha ao carregar meus pedidos', { erro });
      this.resultadoPedidos = { status: 'erro', mensagem: 'falha ao carregar' };
    }
    this.atualizarPedidos();
  }

  private async carregarGarantias(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const garantias = await api.minhasGarantias();
      this.resultadoGarantias = garantias.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: garantias };
    } catch (erro) {
      logger.erro('portal do cliente: falha ao carregar minhas garantias', { erro });
      this.resultadoGarantias = { status: 'erro', mensagem: 'falha ao carregar' };
    }
    this.atualizarGarantias();
  }

  private async carregarSolicitacoes(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const solicitacoes = await api.minhasSolicitacoes();
      this.resultadoSolicitacoes = solicitacoes.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: solicitacoes };
    } catch (erro) {
      logger.erro('portal do cliente: falha ao carregar minhas solicitações', { erro });
      this.resultadoSolicitacoes = { status: 'erro', mensagem: 'falha ao carregar' };
    }
    this.atualizarSolicitacoes();
  }

  private async abrirDetalhePedido(id: string): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    this.pedidoSelecionadoId = id;
    this.pedidoDetalhe = null;
    this.pedidoEventos = [];
    this.carregandoDetalhe = true;
    this.erroDetalhe = '';
    this.atualizarDetalhe();
    try {
      this.pedidoDetalhe = await api.meuPedido(id);
      // Best-effort: se a timeline falhar, o detalhe do pedido continua útil sem ela (mesmo
      // critério de "dado secundário nunca bloqueia a tela inteira" já usado no admin).
      try {
        this.pedidoEventos = await api.meuPedidoEventos(id);
      } catch (erro) {
        logger.erro('portal do cliente: falha ao carregar timeline do pedido', { erro });
        this.pedidoEventos = [];
      }
    } catch (erro) {
      logger.erro('portal do cliente: falha ao carregar detalhe do pedido', { erro });
      this.erroDetalhe = 'não foi possível carregar o detalhe deste pedido';
    } finally {
      this.carregandoDetalhe = false;
      this.atualizarDetalhe();
    }
  }

  private fecharDetalhe(): void {
    this.pedidoSelecionadoId = null;
    this.pedidoDetalhe = null;
    this.pedidoEventos = [];
    this.erroDetalhe = '';
    this.atualizarDetalhe();
  }

  private atualizarPedidos(): void {
    const container = this.root.querySelector('#secao-pedidos');
    if (!container) return;

    if (this.resultadoPedidos.status === 'ok') {
      this.pedidos = this.resultadoPedidos.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = [
        { key: 'status', label: 'Status' },
        { key: 'valor', label: 'Valor', align: 'right' },
        { key: 'criadoEm', label: 'Criado em' },
      ] satisfies PgTableColumn[];
      tabela.rows = this.pedidos.map((p) => ({
        status: rotuloStatusPedido(p.status),
        valor: formatBRL(centavosParaReais(p.valorTotalCentavos)),
        criadoEm: formatarDataHora(p.criadoEm),
      }));
      return;
    }

    this.pedidos = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoPedidos, 'Meus pedidos', 'nenhum pedido encontrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarGarantias(): void {
    const container = this.root.querySelector('#secao-garantias');
    if (!container) return;

    if (this.resultadoGarantias.status === 'ok') {
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = [
        { key: 'status', label: 'Status' },
        { key: 'criadaEm', label: 'Criada em' },
        { key: 'expiraEm', label: 'Expira em' },
      ] satisfies PgTableColumn[];
      tabela.rows = this.resultadoGarantias.dado.map((g) => ({
        status: g.expirada ? 'Expirada' : 'Vigente',
        criadaEm: formatarDataHora(g.criadoEm),
        expiraEm: formatarDataHora(g.expiraEm),
      }));
      return;
    }

    const { variant, titulo, descricao } = estadoVazio(this.resultadoGarantias, 'Minhas garantias', 'nenhuma garantia registrada ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarSolicitacoes(): void {
    const container = this.root.querySelector('#secao-solicitacoes');
    if (!container) return;

    if (this.resultadoSolicitacoes.status === 'ok') {
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = [
        { key: 'canal', label: 'Canal' },
        { key: 'assunto', label: 'Assunto' },
        { key: 'status', label: 'Status' },
        { key: 'criadoEm', label: 'Aberta em' },
      ] satisfies PgTableColumn[];
      tabela.rows = this.resultadoSolicitacoes.dado.map((t) => ({
        canal: ROTULO_CANAL_TICKET[t.canal],
        assunto: t.assunto,
        status: ROTULO_STATUS_TICKET[t.status],
        criadoEm: formatarDataHora(t.criadoEm),
      }));
      return;
    }

    const { variant, titulo, descricao } = estadoVazio(this.resultadoSolicitacoes, 'Minhas solicitações', 'nenhuma solicitação aberta ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarDetalhe(): void {
    const card = this.root.querySelector<HTMLElement>('#card-detalhe');
    const container = this.root.querySelector('#secao-detalhe');
    if (!card || !container) return;

    if (!this.pedidoSelecionadoId) {
      card.style.display = 'none';
      return;
    }
    card.style.display = '';

    if (this.carregandoDetalhe) {
      container.innerHTML = '<pg-empty-state variant="carregando" titulo="Detalhe do pedido"></pg-empty-state>';
      return;
    }

    if (this.erroDetalhe || !this.pedidoDetalhe) {
      container.innerHTML = `
        <pg-empty-state variant="erro" titulo="Detalhe do pedido" descricao="${escaparHtml(this.erroDetalhe || 'pedido não encontrado')}"></pg-empty-state>
        <div class="detalhe-rodape"><pg-button id="botao-fechar-detalhe" variant="ghost">Fechar</pg-button></div>
      `;
      this.root.querySelector('#botao-fechar-detalhe')?.addEventListener('pg-click', () => this.fecharDetalhe());
      return;
    }

    const p = this.pedidoDetalhe;
    container.innerHTML = `
      <div class="detalhe-linha"><span>Status</span><span>${escaparHtml(rotuloStatusPedido(p.status))}</span></div>
      <div class="detalhe-linha"><span>Valor total</span><span>${escaparHtml(formatBRL(centavosParaReais(p.valorTotalCentavos)))}</span></div>
      <div class="detalhe-linha"><span>Criado em</span><span>${escaparHtml(formatarDataHora(p.criadoEm))}</span></div>
      ${p.pagoEm ? `<div class="detalhe-linha"><span>Pago em</span><span>${escaparHtml(formatarDataHora(p.pagoEm))}</span></div>` : ''}
      ${p.canceladoEm ? `<div class="detalhe-linha"><span>Cancelado em</span><span>${escaparHtml(formatarDataHora(p.canceladoEm))}</span></div>` : ''}
      <pg-table></pg-table>
      ${this.renderTimelineEventos()}
      <div class="detalhe-rodape"><pg-button id="botao-fechar-detalhe" variant="ghost">Fechar</pg-button></div>
    `;
    const tabela = container.querySelector('pg-table') as PgTable;
    tabela.columns = [
      { key: 'descricao', label: 'Item' },
      { key: 'quantidade', label: 'Qtd', align: 'right' },
      { key: 'valorUnitario', label: 'Valor unit.', align: 'right' },
    ] satisfies PgTableColumn[];
    tabela.rows = p.itens.map((item) => ({
      descricao: item.descricao,
      quantidade: String(item.quantidade),
      valorUnitario: formatBRL(centavosParaReais(item.valorUnitarioCentavos)),
    }));
    this.root.querySelector('#botao-fechar-detalhe')?.addEventListener('pg-click', () => this.fecharDetalhe());
  }

  /**
   * Histórico operacional do pedido — status + data/hora + mensagem da PG Custom, em ordem
   * cronológica (mesma ordem que `GET /me/pedidos/:id/eventos` já devolve). NUNCA um chat: é uma
   * lista read-only, sem input de resposta do cliente. `notaInterna` não existe em
   * `PedidoEventoPublico` — não tem como vazar aqui.
   */
  private renderTimelineEventos(): string {
    if (this.pedidoEventos.length === 0) return '';
    return `
      <div class="timeline-eventos">
        <h4>Acompanhamento</h4>
        <ul class="timeline-lista">
          ${this.pedidoEventos
            .map(
              (evento) => `
            <li>
              <div class="timeline-cabecalho">
                <span class="timeline-status">${escaparHtml(ROTULO_STATUS_OPERACIONAL[evento.statusOperacional])}</span>
                <span class="timeline-data">${escaparHtml(formatarDataHora(evento.criadoEm))}</span>
              </div>
              ${evento.mensagemCliente ? `<p class="timeline-mensagem">${escaparHtml(evento.mensagemCliente)}</p>` : ''}
            </li>`
            )
            .join('')}
        </ul>
      </div>`;
  }
}

function estadoVazio(
  resultado: Resultado<unknown>,
  titulo: string,
  mensagemVazio: string
): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    case 'indisponivel':
      return { variant: 'indisponivel', titulo, descricao: resultado.motivo };
    default:
      return { variant: 'vazio', titulo, descricao: mensagemVazio };
  }
}

function primeiroNome(nomeCompleto: string): string {
  return nomeCompleto.trim().split(/\s+/)[0] ?? '';
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-portal-cliente-page', ErpPortalClientePage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-portal-cliente-page': ErpPortalClientePage;
  }
}
